import {
  ActionStatus,
  buildSampleWebhookPayload,
  IUpdateSettingsRequest,
  IWebhookEvent,
  WEBHOOK_EVENT_GROUPS,
  WEBHOOK_EVENTS,
  WebhookEventKey,
} from "shared";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { When } from "react-if";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import { classNames } from "../../../../../common/lib/classNames";
import { CheckBox } from "../../../../../common/Atoms/CheckBox";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { Button } from "../../../../../common/Atoms/Button";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { Modal } from "../../../../../common/Atoms/Modal";
import { TextArea } from "../../../../../common/Atoms/TextArea";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useApiRequest } from "../../../../hooks/useApiRequest";
import { env } from "../../../../../common/lib/env";
import { useSendTestWebhookMutation } from "../../../../services/api/webhooksApi/webhooks";

type SampleStatus = ActionStatus.COMPLETE | ActionStatus.FAILED;

interface WebhookEventsProps {
  disabledEvents: WebhookEventKey[];
  onChangeMultiple: (values: Partial<IUpdateSettingsRequest>) => void;
  disabled: boolean;
  /** A test can only go to a saved URL */
  canSendTest: boolean;
}

const isEscalationLifecycle = (key: WebhookEventKey) => key.startsWith(`escalation.`);

export function WebhookEvents({ disabledEvents, onChangeMultiple, disabled, canSendTest }: WebhookEventsProps) {
  const { organisationId } = useParams();
  const [sendTestWebhook, { isLoading: testLoading }] = useSendTestWebhookMutation();

  const [query, setQuery] = useState(``);
  const [selectedKey, setSelectedKey] = useState<WebhookEventKey>(WEBHOOK_EVENTS[0].key);
  const [status, setStatus] = useState<SampleStatus>(ActionStatus.COMPLETE);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const selected = WEBHOOK_EVENTS.find(e => e.key === selectedKey) as IWebhookEvent;
  const isOn = (key: WebhookEventKey) => !disabledEvents.includes(key);
  const enabledCount = WEBHOOK_EVENTS.filter(e => isOn(e.key)).length;

  const payload = useMemo(
    () => JSON.stringify(buildSampleWebhookPayload(selectedKey, status), null, 2),
    [ selectedKey, status ],
  );

  // Children show when they or their parent match, parents show when they or any child match
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (e: IWebhookEvent) => !q || e.label.toLowerCase().includes(q) || e.key.includes(q);

    return WEBHOOK_EVENT_GROUPS
      .map(g => ({
        ...g,
        events: g.events
          .map(e => ({ ...e, children: matches(e) ? e.children : e.children?.filter(matches) }))
          .filter(e => matches(e) || !!e.children?.length),
      }))
      .filter(g => g.events.length);
  }, [ query ]);

  function setOn(key: WebhookEventKey, on: boolean) {
    onChangeMultiple({
      webhookDisabledEvents: on ?
        disabledEvents.filter(k => k !== key) :
        [...disabledEvents, key],
    });
  }

  async function handleSendTest() {
    if (!organisationId) return;

    try {
      const result = await sendTestWebhook({
        organisationId,
        event: selectedKey,
        status,
      }).unwrap();

      if (result.success) {
        toast.success(`Test event sent (status ${result.statusCode})`);
      }
      else {
        toast.error(`Test event failed${result.statusCode ? ` (status ${result.statusCode})` : ``}`);
      }
    }
    catch {
      toast.error(`Could not send test event`);
    }
  }

  function renderRow(event: IWebhookEvent, nested = false) {
    const isSelected = event.key === selectedKey;

    return (
      <div
        key={ event.key }
        role={ `button` }
        tabIndex={ 0 }
        onClick={ () => setSelectedKey(event.key) }
        onKeyDown={ e => {
          if (e.key === `Enter`) {
            setSelectedKey(event.key);
          }
        } }
        className={ classNames(
          `flex items-center gap-2.5 py-1.5 pr-3 cursor-pointer border-l-[3px]`,
          nested ? `pl-9` : `pl-3`,
          isSelected ? `bg-paidniceGreen-50 border-paidniceGreen-600` : `border-transparent hover:bg-gray-50`,
        ) }
      >
        <CheckBox
          name={ event.key }
          checked={ isOn(event.key) }
          onChange={ ({ value }) => setOn(event.key, value) }
          disabled={ disabled }
          stopPropagation
        />
        <span className={ `text-sm text-gray-900` }>
          { event.label }
        </span>
      </div>
    );
  }

  return (
    <>
      <div className={ `flex items-end justify-between pt-3` }>
        <FormLabel>{ `Events` }</FormLabel>
        <Paragraph variant={ `secondary` }>
          { enabledCount === WEBHOOK_EVENTS.length ? `All ${WEBHOOK_EVENTS.length} events on` : `${enabledCount} of ${WEBHOOK_EVENTS.length} events on` }
        </Paragraph>
      </div>

      <div className={ `grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)] border border-gray-200 rounded-md overflow-hidden` }>
        <div className={ `md:border-r border-b md:border-b-0 border-gray-200 flex flex-col min-w-0` }>
          <div className={ `p-2.5 border-b border-gray-200` }>
            <input
              type={ `search` }
              value={ query }
              onChange={ e => setQuery(e.target.value) }
              placeholder={ `Search events` }
              className={ `block w-full h-9 rounded-md border-gray-300 text-sm focus:border-paidniceGreen-500 focus:ring-paidniceGreen-500` }
            />
          </div>

          <div className={ `max-h-[470px] overflow-y-auto pb-2` }>
            {
              groups.map(group => {
                const groupEvents = group.events.flatMap(e => [e, ...(e.children || [])]);

                return (
                  <div key={ group.name }>
                    <div className={ `flex items-center justify-between px-3 pt-2.5 pb-1 text-xs` }>
                      <span className={ `font-semibold text-gray-700` }>{ group.name }</span>
                      <span className={ `text-gray-500` }>
                        { `${groupEvents.filter(e => isOn(e.key)).length}/${groupEvents.length}` }
                      </span>
                    </div>

                    {
                      group.events.map(event => (
                        <div key={ event.key }>
                          { renderRow(event) }
                          { event.children?.map(child => renderRow(child, true)) }
                        </div>
                      ))
                    }
                  </div>
                );
              })
            }

            <When condition={ !groups.length }>
              <Paragraph
                variant={ `secondary` }
                className={ `px-3 pt-3` }
              >
                { `No events match "${query}"` }
              </Paragraph>
            </When>
          </div>
        </div>

        <div className={ `px-4 py-4 flex flex-col gap-3 min-w-0` }>
          <div className={ `flex items-start justify-between gap-3` }>
            <div className={ `flex flex-col gap-0.5` }>
              <span className={ `text-base font-semibold text-gray-900` }>{ selected.label }</span>
              <span className={ `text-xs text-gray-500 font-mono` }>
                {
                  isEscalationLifecycle(selected.key) ?
                    `escalationEvent: "${selected.key.replace(`escalation.`, ``)}"` :
                    `actionType: "${selected.key}"`
                }
              </span>
            </div>

            <Toggle
              label={ `Send` }
              checked={ isOn(selected.key) }
              onChange={ ({ value }) => setOn(selected.key, value) }
              disabled={ disabled }
            />
          </div>

          <Paragraph variant={ `secondary` }>
            { selected.description }
          </Paragraph>

          <div className={ `flex items-center justify-between gap-2` }>
            <div className={ `flex gap-1.5` }>
              <When condition={ !isEscalationLifecycle(selected.key) }>
                {
                  ([[ActionStatus.COMPLETE, `Completed`], [ActionStatus.FAILED, `Failed`]] as const).map(([value, label]) => (
                    <button
                      key={ value }
                      type={ `button` }
                      onClick={ () => setStatus(value) }
                      className={ classNames(
                        `px-2.5 py-1 text-sm font-medium rounded-md border cursor-pointer`,
                        status === value ? `bg-gray-900 border-gray-900 text-white` : `bg-white border-gray-300 text-gray-700 hover:bg-gray-50`,
                      ) }
                    >
                      { label }
                    </button>
                  ))
                }
              </When>
            </div>

            <Button
              size={ `sm` }
              color={ `gray` }
              onClick={ handleSendTest }
              loading={ testLoading }
              disabled={ disabled || !canSendTest }
              tooltip={ canSendTest ? `Sends this sample to your saved webhook URL` : `Save your webhook URL first` }
            >
              { `Send test event` }
            </Button>
          </div>

          <div>
            <button
              type={ `button` }
              onClick={ () => setPayloadOpen(!payloadOpen) }
              className={ `flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer` }
            >
              { payloadOpen ? <ChevronDownIcon className={ `h-4 w-4` } /> : <ChevronRightIcon className={ `h-4 w-4` } /> }
              { `Sample payload` }
            </button>

            <When condition={ payloadOpen }>
              <pre className={ `mt-2 bg-slate-600 dark:bg-[#475569] text-white text-xs leading-relaxed px-3.5 py-3 rounded-md overflow-auto max-h-96 whitespace-pre-wrap` }>
                <code>{ payload }</code>
              </pre>
            </When>
          </div>
        </div>
      </div>

      <div className={ `flex items-center gap-1.5 text-sm text-gray-500` }>
        <span>{ `Need an event that isn't listed?` }</span>
        <button
          type={ `button` }
          onClick={ () => setRequestOpen(true) }
          className={ `font-medium text-paidniceGreen-600 hover:text-paidniceGreen-800 underline underline-offset-2 cursor-pointer` }
        >
          { `Request it` }
        </button>
      </div>

      <RequestWebhookEventModal
        open={ requestOpen }
        setOpen={ setRequestOpen }
      />
    </>
  );
}

function RequestWebhookEventModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const apiRequest = useApiRequest();
  const [text, setText] = useState(``);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function sendRequest() {
    setError(null);
    setLoading(true);

    try {
      await apiRequest({
        method: `POST`,
        url: `${env.apiUrl}/api/v1/organisation/setupRequest`,
        data: {
          setupRequestText: `Webhook event request: ${text}`,
        },
      });

      setSent(true);
      setText(``);
    }
    catch (err) {
      setError(`Failed to send request. Please try again later.`);
      console.error(err);
    }
    finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={ open }
      setOpen={ value => {
        setOpen(value);
        setSent(false);
      } }
      title={ `Request a webhook event` }
      width={ `xl` }
    >
      <div className={ `pl-4` }>
        <When condition={ !!error }>
          <Notification
            type={ `error` }
            className={ `mb-2` }
          >
            { error }
          </Notification>
        </When>

        <When condition={ sent }>
          <Notification type={ `success` }>
            { `Thanks, we have your request and will be in touch.` }
          </Notification>
        </When>

        <When condition={ !sent }>
          <Paragraph variant={ `help` }>
            { `Tell us which event you need and what you would use it for. For example, "When a customer pays an invoice in full, so we can update our CRM."` }
          </Paragraph>

          <TextArea
            rows={ 5 }
            className={ `mt-2` }
            value={ text }
            onChange={ e => setText(e.value?.toString() || ``) }
          />

          <div className={ `flex justify-end mt-4` }>
            <Button
              onClick={ sendRequest }
              disabled={ loading || !text.trim() }
              loading={ loading }
            >
              { `Send request` }
            </Button>
          </div>
        </When>
      </div>
    </Modal>
  );
}
